import { type FC, useEffect, useRef } from 'react';

import { FullscreenComponent, FullscreenMode } from '~src/fullscreen-internals/fullscreen-mode';
import { useIpc } from '~src/utils/ipc-utils';

import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { DialogLayout } from '../../ui/dialog-layout';

interface CommonModalProps {
  title: string;
  ariaTitle: string;
  body: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  onPrimaryButtonClick: () => void;
  onSecondaryButtonClick?: () => void;
  onClose: () => void;
}

const CommonModalFullscreen: FullscreenComponent<CommonModalProps> = ({
  title,
  ariaTitle,
  body,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  onClose,
}) => {
  const ipc = useIpc();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Focus on the button when modal mounts
  useEffect(() => {
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  useEffect(() => {
    ipc.send({
      name: 'set_iframe_title',
      data: {
        title: ariaTitle,
        target: 'fullscreen',
      },
    });

    return () => {
      ipc.send({ name: 'unset_iframe_title', data: { target: 'fullscreen' } });
    };
  }, []);

  return (
    <Dialog width={520} onClose={onClose} wrapProps={{ role: 'alertdialog' }}>
      <DialogLayout title={title} onClose={onClose}>
        {body}
        <div
          css={{
            marginTop: '30px',
          }}
        >
          <Button data-test="primary-button" onClick={() => onPrimaryButtonClick()} ref={buttonRef}>
            {primaryButtonText}
          </Button>
          {secondaryButtonText && (
            <Button
              buttonType="secondary"
              data-test="secondary-button"
              style={{ marginTop: '20px' }}
              onClick={onSecondaryButtonClick ? () => onSecondaryButtonClick() : undefined}
            >
              {secondaryButtonText}
            </Button>
          )}
        </div>
      </DialogLayout>
    </Dialog>
  );
};

const CommonModal: FC<CommonModalProps> = props => {
  return (
    <FullscreenMode>
      <CommonModalFullscreen {...props} />
    </FullscreenMode>
  );
};
CommonModalFullscreen.fullscreenName = 'CommonModalFullscreen';

export { CommonModal, CommonModalFullscreen };
